/**
 * EditableField — inline edit for any text / select / textarea value.
 *
 * Usage:
 *   <EditableField value={scene.location_name}
 *     onSave={v => updateField('location_name', v)} />
 *
 * Props:
 *   value       current value to display / edit
 *   onSave      async fn(newValue) → called on Enter / blur
 *   type        'text' | 'select' | 'textarea' | 'number'
 *   options     [{ value, label }] for type='select'
 *   placeholder shown when empty
 *   className / style  passed to the display span
 *   inputClassName / inputStyle  passed to the input element
 *   disabled    prevents editing
 *   emptyClass  extra className when value is empty (for placeholder styling)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';

export function EditableField({
  value = '',
  onSave,
  type = 'text',
  options = [],
  placeholder = 'Click to edit…',
  className = '',
  style = {},
  inputClassName = '',
  inputStyle = {},
  disabled = false,
  emptyClass = '',
  'data-testid': testid,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null); // 'saved' | 'error'
  const inputRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Sync external value changes (e.g. from context / server push)
  // Using render-time derived state update (React-approved pattern)
  const valueStr = String(value ?? '');
  if (!editing && draft !== valueStr) {
    setDraft(valueStr);
  }

  // Auto-focus when switching to edit mode (no state set in effect)
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  const handleSave = useCallback(async () => {
    const trimmed = draft.trim();
    if (trimmed === String(value ?? '').trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      if (isMounted.current) { setFlash('saved'); setTimeout(() => isMounted.current && setFlash(null), 1200); }
    } catch {
      if (isMounted.current) { setDraft(String(value ?? '')); setFlash('error'); setTimeout(() => isMounted.current && setFlash(null), 1200); }
    } finally {
      if (isMounted.current) { setSaving(false); setEditing(false); }
    }
  }, [draft, value, onSave]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && type !== 'textarea') { e.preventDefault(); handleSave(); }
    if (e.key === 'Escape') { setDraft(String(value ?? '')); setEditing(false); }
  }, [type, value, handleSave]);

  if (disabled) {
    return (
      <span className={className} style={style} data-testid={testid}>
        {value || <span style={{ opacity: 0.35 }}>{placeholder}</span>}
      </span>
    );
  }

  /* ── Edit mode ── */
  if (editing) {
    const base = {
      background: 'rgba(232,197,71,0.07)',
      border: '1px solid rgba(232,197,71,0.35)',
      borderRadius: 4,
      color: 'var(--cb-text)',
      outline: 'none',
      padding: '2px 6px',
      width: '100%',
      boxSizing: 'border-box',
      ...inputStyle,
    };

    if (type === 'select') {
      return (
        <select
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={inputClassName}
          style={{ ...base, cursor: 'pointer' }}
          data-testid={testid ? `${testid}-input` : undefined}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    }

    if (type === 'textarea') {
      return (
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={inputClassName}
          style={{ ...base, minHeight: 64, resize: 'vertical', lineHeight: 1.5 }}
          rows={3}
          data-testid={testid ? `${testid}-input` : undefined}
        />
      );
    }

    return (
      <input
        ref={inputRef}
        type={type === 'number' ? 'number' : 'text'}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={inputClassName}
        style={base}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    );
  }

  /* ── Display mode ── */
  const isEmpty = !value || String(value).trim() === '';
  return (
    <span
      onClick={() => { if (!disabled) setEditing(true); }}
      className={`${className} ${isEmpty ? emptyClass : ''}`}
      style={{
        cursor: disabled ? 'default' : 'text',
        borderRadius: 4,
        transition: 'background 0.12s',
        display: 'inline-block',
        minWidth: 20,
        position: 'relative',
        ...style,
      }}
      title={disabled ? undefined : 'Click to edit'}
      data-testid={testid}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'rgba(232,197,71,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = ''; }}
    >
      {saving && (
        <span style={{ position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)',
                       fontSize: 9, color: '#F5A623', opacity: 0.7 }}>…</span>
      )}
      {flash === 'saved' && (
        <span style={{ position: 'absolute', right: -14, top: '50%', transform: 'translateY(-50%)',
                       fontSize: 9, color: '#4ade80' }}>✓</span>
      )}
      {flash === 'error' && (
        <span style={{ position: 'absolute', right: -14, top: '50%', transform: 'translateY(-50%)',
                       fontSize: 9, color: '#f87171' }}>✗</span>
      )}
      {isEmpty
        ? <span style={{ opacity: 0.3, fontSize: '0.85em' }}>{placeholder}</span>
        : value}
    </span>
  );
}

export default EditableField;
